import React, { useRef, useCallback } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

/**
 * ResultsCharts — Visualization component for TEA and LCA results.
 *
 * Props:
 *   type            – 'tea' | 'lca' | 'compare'
 *   analysisResults – { lca, tea } from calculators
 *   scenarios       – [scenarioA, scenarioB] for compare mode (optional)
 *   projectName     – project name for chart title
 */

const PROCESS_COLORS = [
  '#1565c0', '#2e7d32', '#e65100', '#6a1b9a', '#c62828',
  '#00838f', '#ef6c00', '#4527a0', '#2e7d32', '#ad1457',
  '#00695c', '#f9a825', '#283593', '#d84315', '#558b2f',
];

const copyChartToClipboard = async (chartRef) => {
  if (!chartRef.current) return;
  const canvas = chartRef.current.canvas;
  try {
    const blob = await new Promise((resolve) =>
      canvas.toBlob(resolve, 'image/png', 1.0)
    );
    await navigator.clipboard.write([
      new ClipboardItem({ 'image/png': blob }),
    ]);
    return true;
  } catch (err) {
    console.error('[Chart] Copy failed:', err);
    return false;
  }
};

const CopyButton = ({ chartRef }) => {
  const [copied, setCopied] = React.useState(false);
  const handleCopy = async () => {
    const ok = await copyChartToClipboard(chartRef);
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };
  return (
    <button
      onClick={handleCopy}
      style={{
        padding: '3px 10px', fontSize: '11px', cursor: 'pointer',
        background: copied ? '#2e7d32' : '#f5f5f5',
        color: copied ? '#fff' : '#555',
        border: '1px solid #ccc', borderRadius: 4,
        transition: 'all 0.2s',
      }}
    >
      {copied ? 'Copied!' : 'Copy Chart'}
    </button>
  );
};

// ── TEA Bar Chart ──────────────────────────────────────────────────────────

export const TEABarChart = ({ analysisResults }) => {
  const chartRef = useRef(null);
  const tea = analysisResults?.tea;
  if (!tea || !tea.success || !tea.process_breakdown) return null;

  const breakdown = tea.process_breakdown;
  const entries = Object.entries(breakdown).filter(([, pd]) => pd.total_cost > 0);
  if (entries.length === 0) return null;

  const labels = entries.map(([, pd]) => (pd.process_name || 'Unknown').replace(/_/g, ' '));
  const costs = entries.map(([, pd]) => pd.total_cost);

  const data = {
    labels,
    datasets: [{
      label: 'Cost (USD)',
      data: costs,
      backgroundColor: entries.map((_, i) => PROCESS_COLORS[i % PROCESS_COLORS.length]),
      borderRadius: 3,
      maxBarThickness: 40,
    }],
  };

  const options = {
    indexAxis: 'y',
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      title: { display: true, text: 'Cost by Process', font: { size: 13 }, padding: { bottom: 8 } },
      tooltip: {
        callbacks: {
          label: (ctx) => `$${ctx.parsed.x.toLocaleString(undefined, { maximumFractionDigits: 2 })}`,
        },
      },
    },
    scales: {
      x: {
        title: { display: true, text: 'USD', font: { size: 11 } },
        ticks: { callback: (v) => `$${v.toLocaleString()}` },
        grid: { color: '#f0f0f0' },
      },
      y: {
        ticks: { font: { size: 11 } },
        grid: { display: false },
      },
    },
  };

  const height = Math.max(180, entries.length * 36 + 60);

  return (
    <div style={{ marginTop: '12px' }}>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '4px' }}>
        <CopyButton chartRef={chartRef} />
      </div>
      <div style={{ height: `${height}px`, position: 'relative' }}>
        <Bar ref={chartRef} data={data} options={options} />
      </div>
    </div>
  );
};

// ── LCA Bar Chart ──────────────────────────────────────────────────────────

export const LCABarChart = ({ analysisResults, selectedCategory }) => {
  const chartRef = useRef(null);
  const lca = analysisResults?.lca;
  if (!lca || !lca.results) return null;

  // If a category is selected, show process breakdown for that category
  // Otherwise show totals across all categories
  if (selectedCategory && lca.results[selectedCategory]) {
    const catData = lca.results[selectedCategory];
    const pb = catData.process_breakdown || {};
    const entries = Object.entries(pb).filter(([, p]) => p.total !== 0);
    if (entries.length === 0) return null;

    const labels = entries.map(([, p]) => (p.process_name || 'Unknown').replace(/_/g, ' '));
    const values = entries.map(([, p]) => p.total);

    const data = {
      labels,
      datasets: [{
        label: catData.unit,
        data: values,
        backgroundColor: entries.map((_, i) => PROCESS_COLORS[i % PROCESS_COLORS.length]),
        borderRadius: 3,
        maxBarThickness: 40,
      }],
    };

    const shortLabel = selectedCategory.replace('TRACI v2.1 - ', '');

    const options = {
      indexAxis: 'y',
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        title: { display: true, text: `${shortLabel} by Process`, font: { size: 13 }, padding: { bottom: 8 } },
        tooltip: {
          callbacks: {
            label: (ctx) => {
              const v = ctx.parsed.x;
              const formatted = Math.abs(v) < 0.001 && v !== 0 ? v.toExponential(2) : v.toLocaleString(undefined, { maximumFractionDigits: 4 });
              return `${formatted} ${catData.unit}`;
            },
          },
        },
      },
      scales: {
        x: {
          title: { display: true, text: catData.unit, font: { size: 11 } },
          grid: { color: '#f0f0f0' },
        },
        y: {
          ticks: { font: { size: 11 } },
          grid: { display: false },
        },
      },
    };

    const height = Math.max(180, entries.length * 36 + 60);

    return (
      <div style={{ marginTop: '12px' }}>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '4px' }}>
          <CopyButton chartRef={chartRef} />
        </div>
        <div style={{ height: `${height}px`, position: 'relative' }}>
          <Bar ref={chartRef} data={data} options={options} />
        </div>
      </div>
    );
  }

  // Default: show all TRACI categories as a summary bar chart
  const categories = Object.entries(lca.results);
  if (categories.length === 0) return null;

  // Normalize values to show relative contribution (%) since units differ
  const maxes = categories.map(([, d]) => Math.abs(d.total));
  const overallMax = Math.max(...maxes) || 1;

  const labels = categories.map(([k]) => k.replace('TRACI v2.1 - ', ''));
  const normalizedValues = categories.map(([, d]) => (d.total / overallMax) * 100);

  const data = {
    labels,
    datasets: [{
      label: '% of max category',
      data: normalizedValues,
      backgroundColor: categories.map((_, i) => PROCESS_COLORS[i % PROCESS_COLORS.length]),
      borderRadius: 3,
      maxBarThickness: 40,
    }],
  };

  const options = {
    indexAxis: 'y',
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      title: { display: true, text: 'TRACI v2.1 Impact Categories (Normalized)', font: { size: 13 }, padding: { bottom: 8 } },
      tooltip: {
        callbacks: {
          label: (ctx) => {
            const catEntry = categories[ctx.dataIndex];
            const d = catEntry[1];
            const v = d.total;
            const formatted = Math.abs(v) < 0.001 && v !== 0 ? v.toExponential(2) : v.toLocaleString(undefined, { maximumFractionDigits: 4 });
            return `${formatted} ${d.unit}`;
          },
        },
      },
    },
    scales: {
      x: {
        title: { display: true, text: '% of max', font: { size: 11 } },
        max: 100,
        grid: { color: '#f0f0f0' },
      },
      y: {
        ticks: { font: { size: 11 } },
        grid: { display: false },
      },
    },
  };

  const height = Math.max(200, categories.length * 32 + 60);

  return (
    <div style={{ marginTop: '12px' }}>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '4px' }}>
        <CopyButton chartRef={chartRef} />
      </div>
      <div style={{ height: `${height}px`, position: 'relative' }}>
        <Bar ref={chartRef} data={data} options={options} />
      </div>
    </div>
  );
};

// ── Scenario Comparison Chart ──────────────────────────────────────────────

export const ScenarioCompareChart = ({ scenarioA, scenarioB, mode }) => {
  const chartRef = useRef(null);
  if (!scenarioA || !scenarioB) return null;

  if (mode === 'tea') {
    const teaA = scenarioA.tea;
    const teaB = scenarioB.tea;
    if (!teaA?.success || !teaB?.success) return null;

    const allProcs = new Set([
      ...Object.keys(teaA.process_breakdown || {}),
      ...Object.keys(teaB.process_breakdown || {}),
    ]);

    const labels = [];
    const valuesA = [];
    const valuesB = [];
    for (const procId of allProcs) {
      const pA = teaA.process_breakdown?.[procId];
      const pB = teaB.process_breakdown?.[procId];
      labels.push((pA?.process_name || pB?.process_name || procId).replace(/_/g, ' '));
      valuesA.push(pA?.total_cost || 0);
      valuesB.push(pB?.total_cost || 0);
    }

    const data = {
      labels,
      datasets: [
        {
          label: scenarioA.name,
          data: valuesA,
          backgroundColor: '#1565c0',
          borderRadius: 3,
          maxBarThickness: 24,
        },
        {
          label: scenarioB.name,
          data: valuesB,
          backgroundColor: '#e65100',
          borderRadius: 3,
          maxBarThickness: 24,
        },
      ],
    };

    const options = {
      indexAxis: 'y',
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { position: 'top', labels: { font: { size: 11 } } },
        title: { display: true, text: 'Cost Comparison by Process', font: { size: 13 } },
        tooltip: {
          callbacks: {
            label: (ctx) => `${ctx.dataset.label}: $${ctx.parsed.x.toLocaleString(undefined, { maximumFractionDigits: 2 })}`,
          },
        },
      },
      scales: {
        x: {
          title: { display: true, text: 'USD', font: { size: 11 } },
          ticks: { callback: (v) => `$${v.toLocaleString()}` },
          grid: { color: '#f0f0f0' },
        },
        y: { ticks: { font: { size: 11 } }, grid: { display: false } },
      },
    };

    const height = Math.max(200, labels.length * 50 + 80);

    return (
      <div style={{ marginTop: '12px' }}>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '4px' }}>
          <CopyButton chartRef={chartRef} />
        </div>
        <div style={{ height: `${height}px`, position: 'relative' }}>
          <Bar ref={chartRef} data={data} options={options} />
        </div>
      </div>
    );
  }

  if (mode === 'lca') {
    const lcaA = scenarioA.lca;
    const lcaB = scenarioB.lca;
    if (!lcaA?.results || !lcaB?.results) return null;

    const allCats = new Set([...Object.keys(lcaA.results), ...Object.keys(lcaB.results)]);
    const labels = [];
    const valuesA = [];
    const valuesB = [];

    for (const cat of allCats) {
      labels.push(cat.replace('TRACI v2.1 - ', ''));
      const maxVal = Math.max(
        Math.abs(lcaA.results[cat]?.total || 0),
        Math.abs(lcaB.results[cat]?.total || 0)
      ) || 1;
      valuesA.push(((lcaA.results[cat]?.total || 0) / maxVal) * 100);
      valuesB.push(((lcaB.results[cat]?.total || 0) / maxVal) * 100);
    }

    const data = {
      labels,
      datasets: [
        { label: scenarioA.name, data: valuesA, backgroundColor: '#1565c0', borderRadius: 3, maxBarThickness: 24 },
        { label: scenarioB.name, data: valuesB, backgroundColor: '#e65100', borderRadius: 3, maxBarThickness: 24 },
      ],
    };

    const options = {
      indexAxis: 'y',
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { position: 'top', labels: { font: { size: 11 } } },
        title: { display: true, text: 'LCA Impact Comparison (Normalized per Category)', font: { size: 13 } },
        tooltip: {
          callbacks: {
            label: (ctx) => {
              const cat = [...allCats][ctx.dataIndex];
              const results = ctx.datasetIndex === 0 ? lcaA.results : lcaB.results;
              const d = results[cat];
              if (!d) return `${ctx.dataset.label}: —`;
              const v = d.total;
              const formatted = Math.abs(v) < 0.001 && v !== 0 ? v.toExponential(2) : v.toLocaleString(undefined, { maximumFractionDigits: 4 });
              return `${ctx.dataset.label}: ${formatted} ${d.unit}`;
            },
          },
        },
      },
      scales: {
        x: { title: { display: true, text: '% of max in category', font: { size: 11 } }, max: 100, grid: { color: '#f0f0f0' } },
        y: { ticks: { font: { size: 11 } }, grid: { display: false } },
      },
    };

    const height = Math.max(200, labels.length * 50 + 80);

    return (
      <div style={{ marginTop: '12px' }}>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '4px' }}>
          <CopyButton chartRef={chartRef} />
        </div>
        <div style={{ height: `${height}px`, position: 'relative' }}>
          <Bar ref={chartRef} data={data} options={options} />
        </div>
      </div>
    );
  }

  return null;
};

export default { TEABarChart, LCABarChart, ScenarioCompareChart };
